import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Avatar from '@mui/material/Avatar';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import CircularProgress from '@mui/material/CircularProgress';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import AlertModal from '../components/AlertModal';
import CompetenceMonthField from '../components/CompetenceMonthField';
import ConfirmModal from '../components/ConfirmModal';
import PremiumSelect from '../components/PremiumSelect';
import PageContainer from '../components/ui/PageContainer';
import StatusChip, { StatusTone } from '../components/ui/StatusChip';
import { User as UserType, AlertState } from '../types';
import { getUserPhotoUrl } from '../utils/userPhoto';
import {
  CONTRACT_TYPE_OPTIONS,
  contractTypeLabel,
  fiscalFormError,
  fiscalIncompleteMessage,
  type ContractTypeCode,
} from '../utils/fiscalProfile';
import DeveloperSelfProfile from './DeveloperSelfProfile';

const PROFILE_PHOTO_MAX_BYTES = 4 * 1024 * 1024;

interface UserFormData {
  name: string;
  email: string;
  password: string;
  role: 'admin' | 'manager' | 'developer' | 'hr';
  level: 'junior' | 'pleno' | 'senior';
  managerId: string;
  active: boolean;
  contractType: '' | ContractTypeCode;
  companyDocument: string;
  companyName: string;
  invoiceRequired: boolean;
  invoiceRequiredFrom: string;
  invoiceRequiredUntil: string;
}

const EMPTY_FISCAL_FORM = {
  contractType: '' as const,
  companyDocument: '',
  companyName: '',
  invoiceRequired: false,
  invoiceRequiredFrom: '',
  invoiceRequiredUntil: '',
};

/** Garante comparação com os valores do formulário (API pode variar em casing, tipo ou ordinal). */
function normalizeUserRole(role: unknown): UserFormData['role'] | null {
  if (typeof role === 'number' && Number.isInteger(role)) {
    const byOrdinal: UserFormData['role'][] = ['admin', 'manager', 'developer', 'hr'];
    if (role >= 0 && role < byOrdinal.length) {
      return byOrdinal[role];
    }
  }
  const s = String(role ?? '')
    .trim()
    .toLowerCase();
  if (s === 'admin' || s === 'manager' || s === 'developer' || s === 'hr') {
    return s;
  }
  return null;
}

function normalizeDeveloperLevel(
  level: unknown,
  fallback: UserFormData['level'],
): UserFormData['level'] {
  const s = String(level ?? '')
    .trim()
    .toLowerCase();
  if (s === 'junior' || s === 'pleno' || s === 'senior') {
    return s;
  }
  return fallback;
}

const Users: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user, refreshUser } = authContext || {};
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });
  const [userToDelete, setUserToDelete] = useState<UserType | null>(null);
  const [deleteUserInProgress, setDeleteUserInProgress] = useState(false);
  const [formData, setFormData] = useState<UserFormData>({
    name: '',
    email: '',
    password: '',
    role: 'developer',
    level: 'junior',
    managerId: '',
    active: true,
    ...EMPTY_FISCAL_FORM,
  });
  const userFormSurfaceRef = useRef<HTMLDivElement | null>(null);
  const photoFileInputRef = useRef<HTMLInputElement | null>(null);

  const photoFileStatusText = useMemo((): string => {
    if (photoFile) {
      return photoFile.name;
    }
    if (editingUserId && photoPreview) {
      return 'Foto atual — envie outra imagem para substituir';
    }
    return 'Nenhum arquivo selecionado';
  }, [photoFile, editingUserId, photoPreview]);

  const loadUsers = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<UserType[]>('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'hr')) {
      loadUsers();
    }
  }, [loadUsers, user]);

  useEffect(() => {
    if (!showForm) {
      return;
    }
    const id = window.requestAnimationFrame(() => {
      const el = userFormSurfaceRef.current;
      if (!el) {
        return;
      }
      /* Espaço livre abaixo do header sticky para o topo do formulário ficar visível */
      const headerGapPx = 132;
      const top = el.getBoundingClientRect().top + window.scrollY - headerGapPx;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [showForm, editingUserId]);

  const resetForm = useCallback((): void => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'developer',
      level: 'junior',
      managerId: '',
      active: true,
      ...EMPTY_FISCAL_FORM,
    });
    setEditingUserId(null);
    setPhotoFile(null);
    setPhotoPreview(null);
  }, []);

  const openCreateForm = (): void => {
    if (showForm && !editingUserId) {
      setShowForm(false);
      resetForm();
      return;
    }

    resetForm();
    setShowForm(true);
  };

  const openEditForm = (selectedUser: UserType): void => {
    setEditingUserId(selectedUser.id);
    const role = normalizeUserRole(selectedUser.role) ?? selectedUser.role;
    const level = normalizeDeveloperLevel(selectedUser.level, 'junior');
    setFormData({
      name: selectedUser.name,
      email: selectedUser.email,
      password: '',
      role: role as UserFormData['role'],
      level,
      managerId: selectedUser.manager?.id || '',
      active: selectedUser.active,
      contractType: (selectedUser.contractType || '') as UserFormData['contractType'],
      companyDocument: selectedUser.companyDocument || '',
      companyName: selectedUser.companyName || '',
      invoiceRequired: selectedUser.invoiceRequired === true,
      invoiceRequiredFrom: selectedUser.invoiceRequiredFrom || '',
      invoiceRequiredUntil: selectedUser.invoiceRequiredUntil || '',
    });
    setPhotoFile(null);
    setPhotoPreview(getUserPhotoUrl(selectedUser));
    setShowForm(true);
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];

    if (!file) {
      setPhotoFile(null);
      setPhotoPreview(editingUserId ? photoPreview : null);
      return;
    }

    if (file.size > PROFILE_PHOTO_MAX_BYTES) {
      setAlert({
        isOpen: true,
        message: 'A imagem deve ter no máximo 4 MB. Escolha um arquivo menor.',
        type: 'warning',
      });
      event.target.value = '';
      return;
    }

    if (photoPreview && photoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(photoPreview);
    }

    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const currentEditingUserId = editingUserId;

    const payload = new FormData();
    payload.append('name', formData.name);
    payload.append('email', formData.email);
    payload.append('role', formData.role);
    payload.append('active', String(formData.active));

    if (formData.password) {
      payload.append('password', formData.password);
    }

    if (formData.role === 'developer') {
      payload.append('level', formData.level);
      payload.append('managerId', formData.managerId);
    }

    const fiscalError = fiscalFormError(formData);
    if (fiscalError) {
      setAlert({ isOpen: true, message: fiscalError, type: 'warning' });
      return;
    }

    payload.append('contractType', formData.contractType);
    payload.append('companyDocument', formData.companyDocument);
    payload.append('companyName', formData.companyName);
    payload.append('invoiceRequired', String(formData.invoiceRequired));
    payload.append('invoiceRequiredFrom', formData.invoiceRequiredFrom);
    payload.append('invoiceRequiredUntil', formData.invoiceRequiredUntil);

    if (photoFile) {
      payload.append('photo', photoFile);
    }

    try {
      if (currentEditingUserId) {
        await api.patch(`/users/${currentEditingUserId}`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        if (!formData.password) {
          setAlert({
            isOpen: true,
            message: 'Informe uma senha para criar o usuário.',
            type: 'warning',
          });
          return;
        }

        await api.post('/users', payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setShowForm(false);
      resetForm();
      await loadUsers();

      if (currentEditingUserId === user?.id && refreshUser) {
        await refreshUser();
      }

      setAlert({
        isOpen: true,
        message: currentEditingUserId
          ? 'Usuário atualizado com sucesso!'
          : 'Usuário criado com sucesso!',
        type: 'success',
      });
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message:
          error.response?.data?.message ||
          (currentEditingUserId ? 'Erro ao atualizar usuário' : 'Erro ao criar usuário'),
        type: 'error',
      });
    }
  };

  const requestDeleteUser = (selectedUser: UserType): void => {
    if (selectedUser.id === user?.id) {
      setAlert({
        isOpen: true,
        message: 'Você não pode excluir o próprio usuário logado.',
        type: 'warning',
      });
      return;
    }
    setUserToDelete(selectedUser);
  };

  const cancelDeleteUser = (): void => {
    if (deleteUserInProgress) return;
    setUserToDelete(null);
  };

  const confirmDeleteUser = async (): Promise<void> => {
    if (!userToDelete) return;
    setDeleteUserInProgress(true);
    try {
      await api.delete(`/users/${userToDelete.id}`);
      await loadUsers();
      setUserToDelete(null);
      setAlert({
        isOpen: true,
        message: 'Usuário excluído com sucesso!',
        type: 'success',
      });
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error.response?.data?.message || 'Erro ao excluir usuário',
        type: 'error',
      });
    } finally {
      setDeleteUserInProgress(false);
    }
  };

  const managers = useMemo(() => users.filter((u) => u.role === 'manager'), [users]);
  const totalActiveUsers = useMemo(() => users.filter((u) => u.active).length, [users]);
  const totalManagers = managers.length;
  const totalDevelopers = useMemo(
    () => users.filter((u) => u.role === 'developer').length,
    [users]
  );

  if (user?.role === 'developer') {
    return <DeveloperSelfProfile />;
  }

  if (!user || (user.role !== 'admin' && user.role !== 'hr')) {
    return (
      <PageContainer>
        <Typography variant="body1">Acesso negado</Typography>
      </PageContainer>
    );
  }

  if (loading) {
    return (
      <PageContainer>
        <Stack spacing={2} sx={{ alignItems: 'center', py: 6 }}>
          <CircularProgress />
          <Typography variant="h6">Preparando usuários</Typography>
          <Typography variant="body2" color="text.secondary">
            Organizando perfis, permissões e relacionamentos da equipe.
          </Typography>
        </Stack>
      </PageContainer>
    );
  }

  const showDeveloperFields = normalizeUserRole(formData.role) === 'developer';

  const roleLabel = (role: UserType['role']): string => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'manager':
        return 'Gestor';
      case 'developer':
        return 'Desenvolvedor';
      case 'hr':
        return 'RH';
      default:
        return role;
    }
  };

  const roleTone = (role: UserType['role']): StatusTone => {
    switch (role) {
      case 'admin':
        return 'danger';
      case 'manager':
        return 'accent';
      case 'developer':
        return 'info';
      case 'hr':
        return 'warning';
      default:
        return 'neutral';
    }
  };

  const levelLabel = (level?: UserType['level']): string => {
    if (!level) return 'Não definido';
    switch (level) {
      case 'junior':
        return 'Júnior';
      case 'pleno':
        return 'Pleno';
      case 'senior':
        return 'Sênior';
      default:
        return level;
    }
  };

  const getInitials = (name: string): string =>
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('');

  const stats: Array<{ label: string; value: number; hint: string }> = [
    { label: 'Total de usuários', value: users.length, hint: 'Todos os perfis cadastrados no sistema.' },
    { label: 'Usuários ativos', value: totalActiveUsers, hint: 'Perfis disponíveis para uso e autenticação.' },
    { label: 'Gestores', value: totalManagers, hint: 'Responsáveis por acompanhar o time.' },
    { label: 'Desenvolvedores', value: totalDevelopers, hint: 'Equipe técnica registrada na plataforma.' },
  ];

  return (
    <PageContainer
      icon={<GroupOutlinedIcon />}
      maxWidth="xl"
      title="Usuários e permissões"
      subtitle="Centralize a criação de perfis, organize papéis da equipe e mantenha a estrutura da plataforma sempre atualizada."
      actions={
        <Button
          variant={showForm && !editingUserId ? 'outlined' : 'contained'}
          startIcon={showForm && !editingUserId ? <CloseIcon /> : <AddIcon />}
          onClick={openCreateForm}
        >
          {showForm && !editingUserId ? 'Fechar formulário' : 'Novo usuário'}
        </Button>
      }
    >
      <Grid container spacing={2} sx={{ mb: { xs: 2, md: 3 } }}>
        {stats.map((stat) => (
          <Grid key={stat.label} size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%', border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="overline" color="text.secondary">
                {stat.label}
              </Typography>
              <Typography variant="h4" component="p" sx={{ fontWeight: 800, my: 0.5 }}>
                {stat.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stat.hint}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {showForm && (
        <Paper
          ref={userFormSurfaceRef}
          variant="outlined"
          aria-label={editingUserId ? 'Formulário de edição de usuário' : 'Formulário de novo usuário'}
          sx={{ p: { xs: 2, md: 3 }, mb: { xs: 2, md: 3 }, border: '1px solid', borderColor: 'divider' }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant="overline" color="text.secondary">
              Cadastro
            </Typography>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 800 }}>
              {editingUserId ? 'Editar usuário' : 'Criar novo usuário'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {editingUserId
                ? 'Atualize os dados do perfil, permissões e foto do usuário.'
                : 'Preencha os dados abaixo para liberar acesso ao sistema.'}
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Nome"
                  type="text"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nome completo"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="seu.email@empresa.com"
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={`Senha ${editingUserId ? '(opcional)' : '*'}`}
                  type="password"
                  fullWidth
                  required={!editingUserId}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={
                    editingUserId ? 'Deixe em branco para manter a atual' : 'Defina uma senha segura'
                  }
                  slotProps={{ htmlInput: { autoComplete: 'new-password' } }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <PremiumSelect
                  label="Perfil"
                  value={formData.role}
                  options={[
                    { value: 'developer', label: 'Desenvolvedor' },
                    { value: 'manager', label: 'Gestor/Tech Lead' },
                    { value: 'admin', label: 'Administrador' },
                    { value: 'hr', label: 'RH' },
                  ]}
                  onChange={(value) =>
                    setFormData({ ...formData, role: value as UserFormData['role'] })
                  }
                  required
                />
              </Grid>

              {showDeveloperFields && (
                <>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <PremiumSelect
                      label="Nível"
                      value={formData.level}
                      options={[
                        { value: 'junior', label: 'Júnior' },
                        { value: 'pleno', label: 'Pleno' },
                        { value: 'senior', label: 'Sênior' },
                      ]}
                      onChange={(value) =>
                        setFormData({ ...formData, level: value as UserFormData['level'] })
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <PremiumSelect
                      label="Gestor"
                      value={formData.managerId}
                      options={[
                        { value: '', label: 'Nenhum' },
                        ...managers.map((manager) => ({ value: manager.id, label: manager.name })),
                      ]}
                      onChange={(value) => setFormData({ ...formData, managerId: value })}
                    />
                  </Grid>
                </>
              )}

              <Grid size={{ xs: 12, md: 6 }}>
                <PremiumSelect
                  label="Status"
                  value={formData.active ? 'true' : 'false'}
                  options={[
                    { value: 'true', label: 'Ativo' },
                    { value: 'false', label: 'Inativo' },
                  ]}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      active: value === 'true',
                    })
                  }
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box role="group" aria-labelledby="user-photo-file-label">
                  <Typography
                    id="user-photo-file-label"
                    variant="body2"
                    sx={{ fontWeight: 600, fontSize: '0.85rem', mb: '6px' }}
                  >
                    Foto do usuário{' '}
                    <Typography component="small" variant="caption" color="text.secondary">
                      (máx. 4 MB)
                    </Typography>
                  </Typography>
                  <input
                    ref={photoFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', border: 0 }}
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  <Stack
                    direction="row"
                    spacing={1.5}
                    sx={{ alignItems: 'center', backgroundColor: '#f2f2f2', p: 1 }}
                  >
                    <Button
                      type="button"
                      variant="outlined"
                      aria-label="Escolher arquivo de imagem do usuário"
                      onClick={() => photoFileInputRef.current?.click()}
                    >
                      Escolher arquivo
                    </Button>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      title={photoFileStatusText}
                      aria-live="polite"
                      sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                    {photoFileStatusText}
                  </Typography>
                </Stack>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="overline" color="text.secondary">
                Informações fiscais
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                O tipo de vínculo não altera o perfil de acesso e não liga a obrigação de nota sozinho.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <PremiumSelect
                label="Tipo de vínculo"
                value={formData.contractType}
                options={CONTRACT_TYPE_OPTIONS}
                onChange={(value) =>
                  setFormData({ ...formData, contractType: value as UserFormData['contractType'] })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <PremiumSelect
                label="Obrigatoriedade mensal de nota fiscal"
                value={formData.invoiceRequired ? 'true' : 'false'}
                options={[
                  { value: 'false', label: 'Não' },
                  { value: 'true', label: 'Sim' },
                ]}
                onChange={(value) =>
                  setFormData({ ...formData, invoiceRequired: value === 'true' })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="CNPJ"
                fullWidth
                value={formData.companyDocument}
                onChange={(e) => setFormData({ ...formData, companyDocument: e.target.value })}
                placeholder="Opcional"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Razão social"
                fullWidth
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                placeholder="Opcional"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CompetenceMonthField
                label="Competência inicial"
                fullWidth
                value={formData.invoiceRequiredFrom}
                onChange={(next) => setFormData({ ...formData, invoiceRequiredFrom: next })}
                required={formData.invoiceRequired}
                helperText="Mês/ano de início da obrigação"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CompetenceMonthField
                label="Competência final"
                fullWidth
                value={formData.invoiceRequiredUntil}
                onChange={(next) => setFormData({ ...formData, invoiceRequiredUntil: next })}
                allowEmpty
                helperText="Opcional. Deixe em branco se não houver fim"
              />
            </Grid>
            {fiscalIncompleteMessage(formData) && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="warning.main" sx={{ fontWeight: 700 }}>
                  {fiscalIncompleteMessage(formData)}
                </Typography>
              </Grid>
            )}
          </Grid>

            <Stack
              direction="row"
              spacing={2}
              sx={{ alignItems: 'center', mt: 3, p: 2, backgroundColor: '#f2f2f2' }}
            >
              {photoPreview ? (
                <Box
                  component="img"
                  src={photoPreview}
                  alt="Pré-visualização do usuário"
                  sx={{ width: 72, height: 72, objectFit: 'cover' }}
                />
              ) : (
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 72,
                    height: 72,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#e0e0e0',
                    color: 'text.secondary',
                    fontSize: '0.8rem',
                  }}
                >
                  Foto
                </Box>
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Pré-visualização
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Envie uma imagem para exibir no header do sistema e nos cards de usuários.
                </Typography>
              </Box>
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ justifyContent: 'flex-end', mt: 3 }}
            >
              <Button
                type="button"
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" variant="contained" startIcon={editingUserId ? <SaveIcon /> : <AddIcon />}>
                {editingUserId ? 'Salvar alterações' : 'Criar usuário'}
              </Button>
            </Stack>
          </Box>
        </Paper>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Equipe
        </Typography>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 800 }}>
          Perfis cadastrados
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {users.length} usuário(s) encontrado(s)
        </Typography>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider' }}>
        <Table sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell>Usuário</TableCell>
              <TableCell>Perfil</TableCell>
              <TableCell>Vínculo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Nível</TableCell>
              <TableCell>Gestor</TableCell>
              <TableCell>Criado em</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                    <Avatar
                      src={getUserPhotoUrl(u) || undefined}
                      alt={u.name}
                      variant="square"
                      sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}
                    >
                      {getInitials(u.name)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {u.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {u.email}
                      </Typography>
                    </Box>
                  </Stack>
                </TableCell>
                <TableCell>
                  <StatusChip label={roleLabel(u.role)} tone={roleTone(u.role)} />
                </TableCell>
                <TableCell>
                  <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                    <Typography variant="body2">{contractTypeLabel(u.contractType)}</Typography>
                    {fiscalIncompleteMessage(u) && (
                      <StatusChip label="Fiscal incompleto" tone="warning" />
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <StatusChip
                    label={u.active ? 'Ativo' : 'Inativo'}
                    tone={u.active ? 'success' : 'neutral'}
                  />
                </TableCell>
                <TableCell>{levelLabel(u.level)}</TableCell>
                <TableCell>{u.manager?.name || 'Não definido'}</TableCell>
                <TableCell>{new Date(u.createdAt).toLocaleDateString('pt-BR')}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                    <IconButton
                      aria-label={`Editar ${u.name}`}
                      onClick={() => openEditForm(u)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      aria-label={`Excluir ${u.name}`}
                      color="error"
                      onClick={() => requestDeleteUser(u)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmModal
        isOpen={userToDelete !== null}
        title="Excluir usuário"
        message={
          userToDelete
            ? `Tem certeza de que deseja excluir ${userToDelete.name}? Esta ação não pode ser desfeita.`
            : ''
        }
        confirmLabel={deleteUserInProgress ? 'Excluindo…' : 'Excluir'}
        onConfirm={() => void confirmDeleteUser()}
        onCancel={cancelDeleteUser}
        confirmDisabled={deleteUserInProgress}
      />

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        message={alert.message}
        type={alert.type}
      />
    </PageContainer>
  );
};

export default Users;
